import { useEffect, useRef, useState } from 'react';
import { api, ApiError, parentalApi, type Me, type Profile } from '../api';
import { Avatar } from '../components/Avatar';

/** Profielkeuze = Plex Home-gebruikers, met PIN voor beveiligde profielen. */
export function Profiles({ me, onSelected, onLogout }: { me: Me; onSelected: () => void; onLogout: () => void }) {
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [pinFor, setPinFor] = useState<Profile | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);
  // Kinderslot: van een kinderprofiel naar een volwassen profiel vraagt eerst de ouder-PIN
  const [parentFor, setParentFor] = useState<{ p: Profile; plexPin?: string } | null>(null);
  const [parentPin, setParentPin] = useState('');
  // Account zonder Plex: zelf profielen beheren
  const [local, setLocal] = useState(false);
  const [max, setMax] = useState(5);
  const [managing, setManaging] = useState(false);

  useEffect(() => {
    api.profiles()
      .then((r) => { setProfiles(r.profiles); setLocal(!!r.local); setMax(r.max ?? 5); })
      .catch(() => setError('Profielen ophalen mislukt.'));
  }, []);

  useEffect(() => {
    if (pinFor) pinRef.current?.focus();
  }, [pinFor]);

  async function select(p: Profile, code?: string) {
    if (p.protected && !code) {
      setPinFor(p);
      setPin('');
      setError(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.selectProfile(p.uuid, code);
      onSelected();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'parent_pin_required') {
        setPinFor(null);
        setParentFor({ p, plexPin: code });
        setParentPin('');
      } else if (err instanceof ApiError && (err.code === 'wrong_pin' || err.code === 'pin_required')) {
        setError('Onjuiste PIN.');
        setPin('');
        pinRef.current?.focus();
      } else if (err instanceof ApiError && err.code === 'no_server_access') {
        setError('Dit profiel heeft geen toegang tot de Plex-server.');
      } else {
        setError('Profiel kiezen mislukt.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (parentFor) {
    const go = async () => {
      setBusy(true);
      setError(null);
      try {
        await parentalApi.unlock(parentPin);
        const target = parentFor;
        setParentFor(null);
        await select(target.p, target.plexPin);
      } catch (err) {
        setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Onjuiste ouder-PIN.');
        setParentPin('');
      } finally {
        setBusy(false);
      }
    };
    return (
      <main className="center-screen">
        <Avatar src={parentFor.p.thumb} name={parentFor.p.title} />
        <h1 style={{ fontSize: 'calc(var(--u) * 2.6)' }}>{parentFor.p.title}</h1>
        <form
          onSubmit={(e) => { e.preventDefault(); if (parentPin.length >= 4) void go(); }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'calc(var(--u) * 1.6)' }}
        >
          <label htmlFor="parent-pin" className="muted">Kinderslot: voer de ouder-PIN in</label>
          <input
            id="parent-pin"
            className="pin-input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            maxLength={8}
            value={parentPin}
            onChange={(e) => setParentPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          />
          {error && <p className="error" role="alert">{error}</p>}
          <div style={{ display: 'flex', gap: 'calc(var(--u) * 1)' }}>
            <button type="button" className="btn" onClick={() => { setParentFor(null); setError(null); }}>Terug</button>
            <button type="submit" className="btn btn-primary" disabled={busy || parentPin.length < 4}>Doorgaan</button>
          </div>
        </form>
      </main>
    );
  }

  if (pinFor) {
    return (
      <main className="center-screen">
        <Avatar src={pinFor.thumb} name={pinFor.title} />
        <h1 style={{ fontSize: 'calc(var(--u) * 2.6)' }}>{pinFor.title}</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (pin.length === 4) void select(pinFor, pin);
          }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'calc(var(--u) * 1.6)' }}
        >
          <label htmlFor="pin" className="muted">{local ? 'Voer de pincode van dit profiel in' : 'Voer de Plex-PIN in'}</label>
          <input
            id="pin"
            ref={pinRef}
            className="pin-input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pin}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPin(v);
              if (v.length === 4) void select(pinFor, v);
            }}
          />
          {error && <p className="error" role="alert">{error}</p>}
          <div style={{ display: 'flex', gap: 'calc(var(--u) * 1)' }}>
            <button type="button" className="btn" onClick={() => setPinFor(null)}>Terug</button>
            <button type="submit" className="btn btn-primary" disabled={busy || pin.length !== 4}>Doorgaan</button>
          </div>
        </form>
      </main>
    );
  }

  if (managing && profiles) {
    return <ManageProfiles profiles={profiles} max={max} onChange={setProfiles} onDone={() => setManaging(false)} />;
  }

  return (
    <main className="center-screen">
      <div className="logo">tu<span>media</span></div>
      <h1 style={{ fontSize: 'calc(var(--u) * 3)' }}>Wie kijkt er?</h1>
      {profiles === null && !error && <p className="subtle">Profielen laden…</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {profiles && (
        <div className="profiles" data-focus-group>
          {profiles.map((p, i) => (
            <button
              key={p.uuid}
              className="profile focusable"
              onClick={() => void select(p)}
              disabled={busy}
              data-autofocus={i === 0 ? '' : undefined}
            >
              <Avatar src={p.thumb} name={p.title} />
              <span className="profile-name">{p.title}</span>
              {p.protected && <span className="lock">PIN</span>}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 'calc(var(--u) * 1)', flexWrap: 'wrap', justifyContent: 'center' }}>
        {local && profiles && <button className="btn" onClick={() => setManaging(true)}>Profielen beheren</button>}
        <button className="btn btn-ghost" onClick={onLogout}>Uitloggen ({me.account.username})</button>
      </div>
    </main>
  );
}

/** Profielen van een account zonder Plex: toevoegen, hernoemen, kinderprofiel, pincode, verwijderen. */
function ManageProfiles({ profiles, max, onChange, onDone }: { profiles: Profile[]; max: number; onChange: (p: Profile[]) => void; onDone: () => void }) {
  const [edit, setEdit] = useState<Profile | 'new' | null>(null);
  const [title, setTitle] = useState('');
  const [kid, setKid] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = (p: Profile | 'new') => {
    setEdit(p);
    setTitle(p === 'new' ? '' : p.title);
    setKid(p === 'new' ? false : !!p.kid);
    setPin('');
    setError(null);
  };
  const run = async (fn: () => Promise<{ profiles: Profile[] }>) => {
    setBusy(true);
    setError(null);
    try {
      onChange((await fn()).profiles);
      setEdit(null);
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  };
  if (edit) {
    const isMain = edit !== 'new' && profiles[0]?.uuid === edit.uuid;
    return (
      <main className="center-screen ob-screen">
        <div className="ob-card">
          <h1 className="ob-title">{edit === 'new' ? 'Nieuw profiel' : `Profiel ${edit.title}`}</h1>
          <form className="form-grid ob-form" onSubmit={(e) => {
            e.preventDefault();
            void run(() => (edit === 'new' ? api.addProfile(title, kid) : api.updateProfile(edit.uuid, { title, ...(isMain ? {} : { kid }), ...(pin ? { pin } : {}) })));
          }}>
            <label>Naam<input className="text-input" required maxLength={30} autoFocus value={title} onChange={(e) => setTitle(e.target.value)} /></label>
            {!isMain && (
              <label className="check-row"><input type="checkbox" checked={kid} onChange={(e) => setKid(e.target.checked)} /> Kinderprofiel (alleen titels voor kinderen)</label>
            )}
            {edit !== 'new' && (
              <label>Pincode (4 cijfers, optioneel)
                <input className="text-input" type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder={edit.protected ? 'Ingesteld; nieuwe invullen om te wijzigen' : 'Geen'} />
              </label>
            )}
            {error && <p className="error" role="alert">{error}</p>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={busy || !title.trim() || (pin.length > 0 && pin.length !== 4)}>Opslaan</button>
              {edit !== 'new' && edit.protected && <button className="btn" type="button" disabled={busy} onClick={() => void run(() => api.updateProfile(edit.uuid, { pin: null }))}>Pincode weghalen</button>}
              {edit !== 'new' && !isMain && <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => { if (confirmDelete(edit.title)) void run(() => api.deleteProfile(edit.uuid)); else setError(`Klik nog een keer op Verwijderen om ${edit.title} te verwijderen.`); }}>Verwijderen</button>}
              <button className="btn btn-ghost" type="button" onClick={() => setEdit(null)}>Terug</button>
            </div>
          </form>
        </div>
      </main>
    );
  }
  return (
    <main className="center-screen">
      <h1 style={{ fontSize: 'calc(var(--u) * 3)' }}>Profielen beheren</h1>
      <div className="profiles" data-focus-group>
        {profiles.map((p, i) => (
          <button key={p.uuid} className="profile focusable" onClick={() => open(p)} data-autofocus={i === 0 ? '' : undefined}>
            <Avatar src={p.thumb} name={p.title} />
            <span className="profile-name">{p.title}{p.kid ? ' · kind' : ''}</span>
            <span className="lock">Wijzigen</span>
          </button>
        ))}
        {profiles.length < max && (
          <button className="profile focusable" onClick={() => open('new')}>
            <span className="avatar profile-add" aria-hidden="true">+</span>
            <span className="profile-name">Profiel toevoegen</span>
          </button>
        )}
      </div>
      <p className="subtle">Maximaal {max} profielen. Het eerste profiel is het hoofdprofiel.</p>
      <button className="btn btn-primary" onClick={onDone}>Klaar</button>
    </main>
  );
}

// Bewust geen window.confirm (blokkeert de tv-afstandsbediening): tweede klik bevestigt
let pendingDelete = '';
function confirmDelete(name: string): boolean {
  if (pendingDelete === name) {
    pendingDelete = '';
    return true;
  }
  pendingDelete = name;
  window.setTimeout(() => { if (pendingDelete === name) pendingDelete = ''; }, 4000);
  return false;
}
